/**
 * Toast Notification Composable
 * Reusable toast notification logic
 */

import { ref } from 'vue';

const toasts = ref([]);
let nextId = 1;

export function useToast() {
  /**
   * Show a toast notification
   * @param {object} options - Toast options
   * @param {string} options.message - Toast message
   * @param {string} options.type - Toast type (success, error, warning, info)
   * @param {number} options.duration - Duration in ms (default: 3000)
   */
  function show({ message, type = 'info', duration = 3000 }) {
    const id = nextId++;
    const toast = {
      id,
      message,
      type,
      visible: true,
    };

    toasts.value.push(toast);

    if (duration > 0) {
      setTimeout(() => {
        remove(id);
      }, duration);
    }

    return id;
  }

  /**
   * Show success toast
   * @param {string} message - Toast message
   * @param {number} duration - Duration in ms
   */
  function success(message, duration = 3000) {
    return show({ message, type: 'success', duration });
  }

  /**
   * Show error toast
   * @param {string} message - Toast message
   * @param {number} duration - Duration in ms
   */
  function error(message, duration = 5000) {
    return show({ message, type: 'error', duration });
  }

  /**
   * Show warning toast
   * @param {string} message - Toast message
   * @param {number} duration - Duration in ms
   */
  function warning(message, duration = 4000) {
    return show({ message, type: 'warning', duration });
  }

  /**
   * Show info toast
   * @param {string} message - Toast message
   * @param {number} duration - Duration in ms
   */
  function info(message, duration = 3000) {
    return show({ message, type: 'info', duration });
  }

  /**
   * Remove a toast by ID
   * @param {number} id - Toast ID
   */
  function remove(id) {
    const index = toasts.value.findIndex(t => t.id === id);
    if (index !== -1) {
      toasts.value.splice(index, 1);
    }
  }

  /**
   * Clear all toasts
   */
  function clear() {
    toasts.value = [];
  }

  return {
    toasts,
    show,
    success,
    error,
    warning,
    info,
    remove,
    clear,
  };
}
